import { notifications } from '@mantine/notifications';
import { createElement, Fragment, type ReactNode } from 'react';
import { appText } from 'utils/appLocale';
import { escapeRegExp } from 'utils/escapeRegExp';

type NotificationInput = Parameters<typeof notifications.show>[0];
type NotificationOverrides = Omit<
  NotificationInput,
  'title' | 'message' | 'color'
>;

// The one sanctioned notifications.show call; every helper below routes through it.
function rawShow(
  input: NotificationInput
): ReturnType<typeof notifications.show> {
  // eslint-disable-next-line no-restricted-syntax -- this is the canonical wrapper
  return notifications.show(input);
}

/**
 * Escape hatch for notifications that don't fit the standard success/error/
 * warning/info colors: non-standard colors, and loading/updatable toasts (it
 * returns the notification id for notifications.update/hide). Prefer the
 * notify* helpers for ordinary toasts.
 */
export function notifyRaw(
  input: NotificationInput
): ReturnType<typeof notifications.show> {
  return rawShow(input);
}

// `message` accepts ReactNode (not just string) to match Mantine, since some
// notifications render rich content (e.g. a button to undo the action).
function show(
  title: ReactNode,
  message: ReactNode,
  color: string,
  overrides?: NotificationOverrides
): void {
  rawShow({ title, message, color, ...overrides });
}

export function notifySuccess(
  title: ReactNode,
  message: ReactNode = '',
  overrides?: NotificationOverrides
): void {
  show(title, message, 'green', overrides);
}

export function notifyError(
  title: ReactNode,
  message: ReactNode = '',
  overrides?: NotificationOverrides
): void {
  show(title, message, 'red', overrides);
}

/**
 * Prefix warning toast titles as "Warning: …" (or just "Warning" when empty),
 * in the recruiter display language. Apps without a registered translator
 * (candidate, internal) keep the English prefix.
 */
function withWarningTitlePrefix(title: ReactNode): ReactNode {
  const word = appText('status.warning', 'Warning');
  const prefixed = (rest: string) =>
    appText('notify.warningTitle', 'Warning: {{title}}', { title: rest });
  if (typeof title === 'string') {
    const trimmed = title.trim();
    if (!trimmed) {
      return word;
    }
    // A title that already carries the prefix (English or translated) keeps one.
    const existingPrefix = new RegExp(
      `^(warning|${escapeRegExp(word)})\\s*:\\s*`,
      'i'
    );
    return prefixed(trimmed.replace(existingPrefix, ''));
  }
  if (title == null || title === false || title === true) {
    return word;
  }
  return createElement(Fragment, null, prefixed(''), title);
}

export function notifyWarning(
  title: ReactNode,
  message: ReactNode = '',
  overrides?: NotificationOverrides
): void {
  show(withWarningTitlePrefix(title), message, 'yellow', overrides);
}

export function notifyInfo(
  title: ReactNode,
  message: ReactNode = '',
  overrides?: NotificationOverrides
): void {
  show(title, message, 'blue', overrides);
}

// Keep notification callers on the shared error formatter.
export { extractErrorMessage } from 'utils/apiError';
